import { GanttTask } from '../../types/gantt.types'
import { ShipLedgerRow, shipMarkerSpan, shipMarkerChipText, shipReceivedPhotoLabel } from '../../constants/ship.logic'
import { daysBetween } from '../../constants/gantt.logic'
import { ShipGanttMarkerSpan } from '../ship/ShipGanttMarkerSpan'
// 막대 한 개: 발주·입고 마커 · 막대 · 리사이즈 핸들 · 드래그 라벨 · 진행 오버레이

export type GanttBarProps = {
  showMarkers: boolean
  sigOn: boolean
  task: GanttTask
  ganttStart: Date
  DAYS: { lbl: string; dow: string; su: boolean; sa: boolean; td: boolean; holiday: boolean; }[]
  draggingMkId: { taskId: number; type: "order" | "delivery"; } | null
  draggingMkDelta: number
  totalDays: number
  shipRowsByTask: Map<number, ShipLedgerRow[]>
  DW: number
  onMkMouseDown: (e: React.MouseEvent, taskId: number, type: "order" | "delivery") => void
  l: number
  hideBars: boolean
  firstBarTaskId: number | null
  onBarMouseDown: (e: React.MouseEvent, taskId: number) => void
  setCtxMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; taskId: number; taskName: string; taskEndDate: Date; } | null>>
  barTop: number
  startOff: number
  span: number
  isDragging: boolean
  isResizing: boolean
  isPending: boolean
  /** 상세창이 열린 공종의 막대 — 토큰 색 테두리로 강조(B-14) */
  isSelected: boolean
  /** 딥링크 card=bar 로 강조된 막대 — 같은 토큰 색 외곽선, 바깥으로 1px 띄움(F-23). 다음 조작에서 해제 */
  isFocused: boolean
  onBarResizeMouseDown: (e: React.MouseEvent, taskId: number) => void
  /** 끌기 중 안내 'M/D → M/D (N일)' — 계산할 수 없으면 null */
  moveTip: string | null
  /** 리사이즈 중 안내 '소요일 N일 · M/D → M/D' — 계산할 수 없으면 null */
  resizeTip: string | null
}

export function GanttBar({
  showMarkers, sigOn, task, ganttStart, DAYS, draggingMkId, draggingMkDelta, totalDays, shipRowsByTask, DW,
  onMkMouseDown, l, hideBars, firstBarTaskId, onBarMouseDown, setCtxMenu, barTop, startOff, span, isDragging,
  isResizing, isPending, isSelected, isFocused, onBarResizeMouseDown, moveTip, resizeTip,
}: GanttBarProps) {
  const orderBaseIdx    = showMarkers && task.orderDate    ? daysBetween(ganttStart, new Date(task.orderDate))    : -1
  // 자재파트너가 시공하는 공종은 입고·하차사진 대상이 아니라 입고 마커·점선·'N일' 칩·카메라 아이콘을 그리지 않는다(발주 마커는 유지)
  const deliveryBaseIdx = showMarkers && !task.constructByVendor && task.deliveryDate ? daysBetween(ganttStart, new Date(task.deliveryDate)) : -1
  const isOrderCell    = orderBaseIdx    >= 0 && orderBaseIdx    < DAYS.length
  const isDeliveryCell = deliveryBaseIdx >= 0 && deliveryBaseIdx < DAYS.length
  const isOrderDragging    = draggingMkId?.taskId === task.taskId && draggingMkId?.type === 'order'
  const isDeliveryDragging = draggingMkId?.taskId === task.taskId && draggingMkId?.type === 'delivery'

  // 발주~입고 점선·'N일' 칩(W5-26) — 드래그 중인 마커는 이동량을 반영
  const mkOrderIdx = orderBaseIdx >= 0 && isOrderDragging ? orderBaseIdx + draggingMkDelta : orderBaseIdx
  const mkDeliveryIdx = deliveryBaseIdx >= 0 && isDeliveryDragging ? deliveryBaseIdx + draggingMkDelta : deliveryBaseIdx
  // 발주일·입고일 중 하나라도 없으면 점선·칩을 그리지 않는다(shipMarkerSpan은 호출 측에서 거르도록 규정)
  const mkSpan = task.orderDate && task.deliveryDate && showMarkers && !task.constructByVendor
    ? shipMarkerSpan({ orderIdx: mkOrderIdx, deliveryIdx: mkDeliveryIdx, totalDays })
    : null
  // 입고 완료 행(원장 RECEIVED) — 사진 있으면 ● 옆 아이콘, ● 드래그는 막는다(D2·D6-b)
  const shipTaskRows = task.constructByVendor ? undefined : shipRowsByTask.get(task.taskId)
  const receivedRow = shipTaskRows?.find(r => r.orderStatusCode === 'RECEIVED')
  const receivedPhotoRow = shipTaskRows?.find(r => r.orderStatusCode === 'RECEIVED' && (r.receipt?.photoCnt ?? 0) > 0)
  const receivedPhotoCnt = receivedPhotoRow?.receipt?.photoCnt ?? 0

  return (
    <>
      {mkSpan ? (
        <ShipGanttMarkerSpan
          fromIdx={mkSpan.fromIdx}
          toIdx={mkSpan.toIdx}
          days={mkSpan.days}
          showChip={mkSpan.showChip}
          DW={DW}
          dayLabel={shipMarkerChipText(mkSpan.days)}
          cameraIdx={isDeliveryCell && receivedPhotoCnt > 0 ? deliveryBaseIdx : null}
          photoCnt={receivedPhotoCnt}
          cameraLabel={shipReceivedPhotoLabel(receivedPhotoCnt)}
        />
      ) : isDeliveryCell && receivedPhotoCnt > 0 ? (
        <ShipGanttMarkerSpan
          fromIdx={deliveryBaseIdx}
          toIdx={deliveryBaseIdx}
          days={0}
          showChip={false}
          DW={DW}
          dayLabel=""
          cameraIdx={deliveryBaseIdx}
          photoCnt={receivedPhotoCnt}
          cameraLabel={shipReceivedPhotoLabel(receivedPhotoCnt)}
        />
      ) : null}
      {isOrderCell && (
        <div
          onMouseDown={e => onMkMouseDown(e, task.taskId, 'order')}
          style={{ position: 'absolute', top: '50%', left: orderBaseIdx * DW + DW / 2, transform: isOrderDragging ? `translate(calc(-50% + ${draggingMkDelta * DW}px), -50%)` : 'translate(-50%, -50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: isOrderDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
        >
          <svg width="11" height="11" viewBox="0 0 12 12" style={{ filter: 'drop-shadow(0 1px 2px var(--alpha-black-300))' }}><polygon points="6,0 12,6 6,12 0,6" fill="var(--gt-primary)" /></svg>
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--gt-primary)', whiteSpace: 'nowrap', letterSpacing: -0.2, lineHeight: 1 }}>발주</span>
        </div>
      )}
      {isDeliveryCell && (
        <div
          onMouseDown={receivedRow ? undefined : (e => onMkMouseDown(e, task.taskId, 'delivery'))}
          style={{ position: 'absolute', top: '50%', left: deliveryBaseIdx * DW + DW / 2, transform: isDeliveryDragging ? `translate(calc(-50% + ${draggingMkDelta * DW}px), -50%)` : 'translate(-50%, -50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: receivedRow ? 'default' : (isDeliveryDragging ? 'grabbing' : 'grab'), userSelect: 'none' }}
        >
          <div style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--shell-on-surface)', boxShadow: 'var(--elevation-1)' }} />
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap', letterSpacing: -0.2, lineHeight: 1 }}>입고</span>
        </div>
      )}

      {/* 모바일 AnimatedContainer 대응: 바 — 바 숨기기가 켜지면 그리지 않는다 */}
      {l > 0 && (!hideBars || sigOn) && (
      <div
        data-tour={task.taskId === firstBarTaskId ? 'first-bar' : undefined}
        data-gantt-bar={task.taskId}
        onMouseDown={e => onBarMouseDown(e, task.taskId)}
        onContextMenu={e => {
          e.preventDefault()
          e.stopPropagation()
          setCtxMenu({ x: e.clientX, y: e.clientY, taskId: task.taskId, taskName: task.workTypeName, taskEndDate: task.endDate })
        }}
        style={{
          position:   'absolute',
          height:     20,
          borderRadius: 4,
          top:        barTop,
          left:       startOff * DW + 1,
          width:      span * DW - 2,
          background: task.color,
          opacity:    isDragging || isResizing ? 0.6 : 1,
          border:     'none',
          boxShadow:  isDragging || isResizing
            ? `0 2px 8px ${task.color}66`
            : isPending
              ? `0 2px 8px ${task.color}50`
              : 'var(--elevation-2)',
          outline:    isSelected || isFocused ? '2px solid var(--shell-on-surface)' : 'none',
          outlineOffset: isFocused && !isSelected ? 1 : 0,
          display:    'flex', alignItems: 'center', padding: '0 6px',
          fontSize:   9.5, color: 'var(--shell-surface)', fontWeight: 700,
          whiteSpace: 'nowrap', overflow: 'hidden', zIndex: 1,
          cursor:     'grab',
          transition: isDragging || isResizing ? 'none' : 'box-shadow .15s, opacity .15s',
          userSelect: 'none',
        }}
      >
        <svg width="6" height="10" viewBox="0 0 6 10" style={{ color: 'var(--alpha-white-900)', opacity: 0.45, flexShrink: 0, pointerEvents: 'none', marginRight: 4 }}><circle cx="1.5" cy="2" r="1.2" fill="currentColor"/><circle cx="4.5" cy="2" r="1.2" fill="currentColor"/><circle cx="1.5" cy="5" r="1.2" fill="currentColor"/><circle cx="4.5" cy="5" r="1.2" fill="currentColor"/><circle cx="1.5" cy="8" r="1.2" fill="currentColor"/><circle cx="4.5" cy="8" r="1.2" fill="currentColor"/></svg>
        {/* 바 오른쪽 끝 리사이즈 핸들 — 시작일은 그대로 두고 소요일(종료일)만 늘리거나 줄인다 */}
        <div
          onMouseDown={e => onBarResizeMouseDown(e, task.taskId)}
          title="드래그해서 소요일 변경"
          style={{
            position: 'absolute', top: 0, right: 0, width: 7, height: '100%',
            cursor: 'ew-resize', zIndex: 2,
            background: isResizing ? 'var(--alpha-white-500)' : 'var(--alpha-white-300)',
            borderRadius: '0 4px 4px 0',
          }}
        />
      </div>
      )}

      {/* 드래그 중에는 바 위에 공종명을 띄운다 — 공종이 많아 세로로 길어졌을 때도
          지금 옮기는 바가 어떤 공종인지 눈으로 바로 확인할 수 있게. 끌기 중 'M/D → M/D (N일)'을 덧붙이고,
          리사이즈 중에는 같은 자리에 '소요일 N일 · M/D → M/D'를 띄운다(A-12). */}
      {(isDragging || (isResizing && resizeTip)) && (
        <div style={{
          position:      'absolute',
          top:           barTop - 24,
          left:          startOff * DW + (span * DW) / 2,
          transform:     'translateX(-50%)',
          background:    'var(--shell-on-surface)',
          color:         'var(--shell-surface)',
          fontSize:      10.5,
          fontWeight:    700,
          padding:       '3px 9px',
          borderRadius:  24,
          whiteSpace:    'nowrap',
          pointerEvents: 'none',
          boxShadow:     'var(--elevation-2)',
          zIndex:        20,
        }}>
          {isDragging ? `${task.workTypeName}${moveTip ? ` · ${moveTip}` : ''}` : resizeTip}
        </div>
      )}

      {/* 모바일 progress 오버레이 */}
      {l > 0 && task.progress > 0 && (
        <div style={{
          position:     'absolute',
          height:       20,
          borderRadius: 4,
          top:          barTop,
          left:         startOff * DW + 1,
          width:        (span * DW - 2) * task.progress / 100,
          background:   'var(--alpha-black-200)',
          zIndex:       2,
          pointerEvents: 'none',
        }} />
      )}
    </>
  )
}
